import type {
  BookedFlightItineraryWithDepartureTime,
  FlightItinerarySegment,
} from "@b2bportal/air-booking-api";
import type { TripSlice } from "@b2bportal/air-disruption-api";
import {
  DisruptionExerciseInformationCard,
  DisruptionExerciseMoreInfo,
  DisruptionExercisePage,
  DisruptionExercisePageContent,
  DisruptionExerciseSummaryCard,
} from "@b2bportal/core-disruption";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreDisruptionComponent } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DisruptionExerciseMoreInfoType } from "../../components/DisruptionExerciseMoreInfo/DisruptionExerciseMoreInfo";
import { DisruptionExercisePageWidth } from "../../components/DisruptionExercisePage/DisruptionExercisePage";
import defaultStyles from "./DisruptionExerciseRebookOnboardingPage.module.scss";
import { useDisruptionRebookOnboarding } from "./useDisruptionRebookOnboarding";

export interface DisruptionExerciseRebookOnboardingPageProps {
  disruptionProductName: string;
  disruptionProductLogo: string;
  disruptedFlight: BookedFlightItineraryWithDepartureTime;
  rebookingLimit?: string;
  contractId?: string;
  bookingReturn: boolean;
  termsLink: string;
  displayExternalLinkAsModal: boolean;
  delayHours: string;
  hoursString: string;
  onFlightSelectContinue: (props: {
    origin: string;
    destination: string;
    departure: string;
    tripSlice?: TripSlice;
  }) => void;
  onContactSupport: () => void;
  handleRefundClick: () => void;
  setRebookPlanResponse: (DisruptionRebookPlanResponse) => void;
  setSelectedSlice: (slice: {
    segments: Array<FlightItinerarySegment>;
  }) => void;
  showSupportPhone?: boolean;
  cancelFlowAction?: () => void;
}

export const DisruptionExerciseRebookOnboardingPage = ({
  disruptionProductName,
  disruptionProductLogo,
  disruptedFlight,
  rebookingLimit,
  contractId,
  bookingReturn = false,
  termsLink,
  delayHours,
  hoursString,
  onFlightSelectContinue,
  onContactSupport,
  handleRefundClick,
  setRebookPlanResponse,
  setSelectedSlice,
  showSupportPhone = false,
  cancelFlowAction,
}: DisruptionExerciseRebookOnboardingPageProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const COMPONENT_KEY =
    CoreDisruptionComponent.DisruptionExerciseNotEligiblePage;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const {
    loading,
    rebookPlanError,
    openBagsLearnMore,
    openReturnLearnMore,
    openMoreHelp,
    handleCta,
    handleGoBack,
    setRebookPlanError,
    onCloseBagsLearnMore,
    onCloseReturnLearnMore,
    onOpenReturnLearnMore,
    onOpenBagsLearnMore,
    onCloseMoreHelp,
    resetExerciseState,
  } = useDisruptionRebookOnboarding({
    contractId,
    disruptedFlight,
    bookingReturn,
    onFlightSelectContinue,
    setRebookPlanResponse,
    setSelectedSlice,
  });

  const bullets = [
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletOne",
        {
          maxPerPax: rebookingLimit,
        }
      ),
      index: 1,
    },
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletTwo"
      ),
      index: 2,
    },
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletThree"
      ),
      learnMore: {
        title: t("core-disruption.disruptionUniversalExercise.learnMore"),
        onClick: onOpenBagsLearnMore,
      },
      index: 3,
    },
    {
      text: t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.importantInfo.bulletFourWithRebookLimit",
        {
          rebookLimit: rebookingLimit,
        }
      ),
      learnMore: {
        title: t("core-disruption.disruptionUniversalExercise.learnMore"),
        onClick: onOpenReturnLearnMore,
      },
      index: 4,
    },
  ];

  const bagsLearnMore = (
    <DisruptionExerciseMoreInfo
      open={openBagsLearnMore}
      onClose={onCloseBagsLearnMore}
      type={DisruptionExerciseMoreInfoType.Baggage}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      rebookingLimit={rebookingLimit}
      disruptionProductName={disruptionProductName}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
      showSupportPhone={showSupportPhone}
    />
  );

  const returnLearnMore = (
    <DisruptionExerciseMoreInfo
      open={openReturnLearnMore}
      onClose={onCloseReturnLearnMore}
      type={DisruptionExerciseMoreInfoType.Return}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      rebookingLimit={rebookingLimit}
      disruptionProductName={disruptionProductName}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
      showSupportPhone={showSupportPhone}
    />
  );

  const moreHelpPopup = (
    <DisruptionExerciseMoreInfo
      open={openMoreHelp}
      onClose={onCloseMoreHelp}
      type={DisruptionExerciseMoreInfoType.MoreHelp}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      rebookingLimit={rebookingLimit}
      disruptionProductName={disruptionProductName}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
      showSupportPhone={showSupportPhone}
    />
  );

  return (
    <DisruptionExercisePage
      className={cn("DisruptionExerciseRebookOnboardingPage")}
      pageWidth={DisruptionExercisePageWidth.Regular}
      title={disruptionProductName}
      disruptionProductLogo={disruptionProductLogo}
      onBack={handleGoBack}
      openErrorModal={rebookPlanError}
      onCloseErrorModal={() => {
        setRebookPlanError(false);
        resetExerciseState();
      }}
      onSupportClick={onContactSupport}
      handleRefundClick={handleRefundClick}
      submitButtonTitle={t(
        "core-disruption.disruptionUniversalExercise.continue"
      )}
      submitButtonAction={handleCta}
      submitButtonDisabled={false}
      isLoading={loading}
      loadingMessage={t(
        "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.loadingMessage"
      )}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
      onCancelFlow={cancelFlowAction}
      content={
        <DisruptionExercisePageContent
          leftContent={[
            <DisruptionExerciseSummaryCard
              title={t(
                "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.title"
              )}
              description={t(
                "core-disruption.disruptionUniversalExercise.rebook.rebookOnboardingPage.subtitle"
              )}
            />,
            !isDesktopAndUp ? <hr /> : <></>,
            <DisruptionExerciseInformationCard
              onSupportClick={onContactSupport}
              bulletPoints={bullets}
              onRefundClick={handleRefundClick}
            />,
            bagsLearnMore,
            returnLearnMore,
            moreHelpPopup,
          ]}
        />
      }
    />
  );
};

export default DisruptionExerciseRebookOnboardingPage;
